import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { navigateTo } from '../../../libs/history';
import { getTrackedInternalLinkUrl } from '../../../libs/tracking';
import { formatCountdownMsTime } from '../../../libs/utils';
import { useTrackedImpression } from '../../../scripts/hooks/useTrackedImpression';
import { asset } from '../../../services/assetResolverService';
import { useMedia } from '../../grid/hooks';
import { Link } from '../../link';
import CountdownTimer from '../../presentational/countdownTimer';
import { Typography } from '../../typography';

type TMdiMidnightGroupsUpsellProps = {
  className?: string;
};

type TMdiGroupVariant = {
  label: string;
  analyticsKey: string;
  broadcasts: TMdiGroupBroadcast[];
  watchUrl: string;
  bracketUrl: string;
};

type TMdiGroupBroadcast = {
  startsAt: string;
  endsAt: string;
};

type TMdiGroupStatus = 'upcoming' | 'live' | 'completed';
type TMdiGroupClickPart = 'body' | 'button' | 'bracket';

type TUpsellTranslations = {
  startsInLabel: string;
  dayStartsInLabel: string;
  headline: string;
  watchPrompt: string;
  supportingLine: string;
  liveSupportingLine: string;
  liveButton: string;
  upcomingButton: string;
  completedButton: string;
  bracketLink: string;
  logoAlt: string;
};

const NAMESPACE = 'dyn_mdi_midnight_groups_upsell';
const ANALYTICS_PREFIX = 'mdi_midnight_groups_upsell';
const LOGO_PATH = '/images/events/expansion11/mdi-midnight-season-1/mdi_logo.png';
const BACKGROUND_PATH = '/images/events/expansion11/mdi-midnight-season-1/mdi_bg.jpg';
const WATCH_URL = '/tournaments/mdi';

const GROUP_VARIANTS: TMdiGroupVariant[] = [
  {
    label: 'Group A',
    analyticsKey: 'group_a',
    broadcasts: [
      {
        startsAt: '2026-05-08T17:00:00Z',
        endsAt: '2026-05-09T02:00:00Z'
      },
      {
        startsAt: '2026-05-09T17:00:00Z',
        endsAt: '2026-05-10T02:00:00Z'
      },
      {
        startsAt: '2026-05-10T17:00:00Z',
        endsAt: '2026-05-11T02:00:00Z'
      }
    ],
    watchUrl: WATCH_URL,
    bracketUrl: '/events/mdi-midnight-season-1/brackets/group-a'
  },
  {
    label: 'Group B',
    analyticsKey: 'group_b',
    broadcasts: [
      {
        startsAt: '2026-05-15T17:00:00Z',
        endsAt: '2026-05-16T02:00:00Z'
      },
      {
        startsAt: '2026-05-16T17:00:00Z',
        endsAt: '2026-05-17T02:00:00Z'
      },
      {
        startsAt: '2026-05-17T17:00:00Z',
        endsAt: '2026-05-18T02:00:00Z'
      }
    ],
    watchUrl: WATCH_URL,
    bracketUrl: '/events/mdi-midnight-season-1/brackets/group-b'
  },
  {
    label: 'Group C',
    analyticsKey: 'group_c',
    broadcasts: [
      {
        startsAt: '2026-05-22T17:00:00Z',
        endsAt: '2026-05-23T02:00:00Z'
      },
      {
        startsAt: '2026-05-23T17:00:00Z',
        endsAt: '2026-05-24T02:00:00Z'
      },
      {
        startsAt: '2026-05-24T17:00:00Z',
        endsAt: '2026-05-25T02:00:00Z'
      }
    ],
    watchUrl: WATCH_URL,
    bracketUrl: '/events/mdi-midnight-season-1/brackets/group-c'
  }
];

const GROUP_VARIANTS_BY_START = [...GROUP_VARIANTS].sort(
  (left, right) => Date.parse(left.broadcasts[0].startsAt) - Date.parse(right.broadcasts[0].startsAt)
);

const EN_TRANSLATIONS: TUpsellTranslations = {
  startsInLabel: 'MDI {{label}} Starts in',
  dayStartsInLabel: 'MDI {{label}} Day {{dayNumber}} Starts in',
  headline: 'Which teams will survive MDI {{label}} and reach the Season Finals?',
  watchPrompt: 'Watch MDI {{label}} Live',
  supportingLine:
    'Eight teams <0>race head-to-head</0> through <1>MDI {{label}}</1> all weekend! Watch the broadcast and <2>follow the race</2> to the Season Finals.',
  liveSupportingLine:
    '<0>Which teams will survive <1>MDI {{label}}</1>?</0> Watch all eight <0>speedrun head-to-head</0> for a spot in the Season Finals!',
  liveButton: 'WATCH LIVE',
  upcomingButton: 'WATCH LIVE',
  completedButton: 'VIEW GROUP BRACKET',
  bracketLink: 'View {{label}} Bracket',
  logoAlt: 'Mythic Dungeon International logo'
};

const UPSELL_TRANSLATIONS: Record<string, TUpsellTranslations> = {
  en: EN_TRANSLATIONS,
  'en-us': EN_TRANSLATIONS
};

const panelStyle: React.CSSProperties = {
  border: '1px solid rgba(86, 202, 255, 0.78)',
  borderRadius: '0.25rem',
  backgroundColor: '#061327',
  backgroundSize: 'cover',
  backgroundPosition: 'bottom center',
  backgroundRepeat: 'no-repeat',
  color: '#fff',
  cursor: 'pointer',
  columnGap: '0.75rem',
  overflow: 'hidden'
};

const logoWrapStyle: React.CSSProperties = {
  width: 124,
  minWidth: 124,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginLeft: '-0.1rem'
};

const logoStyle: React.CSSProperties = {
  display: 'block',
  width: 118,
  height: 118,
  objectFit: 'contain',
  filter: 'drop-shadow(0 8px 18px rgba(0, 0, 0, 0.34))'
};

const contentStyle: React.CSSProperties = {
  flex: '1 1 auto',
  maxWidth: '48rem',
  minWidth: 0
};

const countdownBlockStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '0.65rem',
  marginBottom: '0.38rem'
};

const countdownLabelStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.32rem',
  color: '#ffe29a',
  lineHeight: 1.05
};

const countdownValueStyle: React.CSSProperties = {
  fontVariantNumeric: 'tabular-nums',
  lineHeight: 1,
  textShadow: '0 0 12px rgba(86, 202, 255, 0.24)'
};

const liveLabelBlockStyle: React.CSSProperties = {
  marginBottom: '0.38rem'
};

const headlineStyle: React.CSSProperties = {
  lineHeight: 1.08,
  maxWidth: '39rem',
  marginBottom: '0.36rem'
};

const liveWatchPromptStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.32rem',
  color: '#ffe29a',
  lineHeight: 1.1
};

const supportingLineStyle: React.CSSProperties = {
  lineHeight: 1.18,
  maxWidth: '39rem',
  color: '#e7f6fb'
};

const buttonGroupStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.35rem',
  alignItems: 'stretch'
};

const primaryButtonStyle: React.CSSProperties = {
  backgroundColor: '#ce9908',
  minWidth: 156
};

const supportLinkStyle: React.CSSProperties = {
  color: 'rgba(218, 246, 255, 0.93)',
  textAlign: 'center',
  textShadow: '0 0 6px rgba(91, 211, 255, 0.12)'
};

const ctaButtonClassName = [
  'slds-button',
  'slds-button--raiderio',
  'text-white',
  'slds-p-vertical--x-small',
  'slds-p-horizontal--small',
  'text-bold',
  'rio-text--title',
  'slds-text-heading--medium',
  'text-nowrap'
].join(' ');

const secondaryLinkClassName = ['slds-text-body--small', 'text-bold', 'rio-realm-link'].join(' ');

export function getMdiMidnightGroupStatus(variant: TMdiGroupVariant, now: number): TMdiGroupStatus {
  const liveBroadcast = getLiveMdiMidnightGroupBroadcast(variant, now);
  if (liveBroadcast) {
    return 'live';
  }

  const nextBroadcast = getNextMdiMidnightGroupBroadcast(variant, now);
  if (nextBroadcast) {
    return 'upcoming';
  }

  return 'completed';
}

export function getSelectedMdiMidnightGroupVariant(now: number) {
  const activeVariant = GROUP_VARIANTS_BY_START.find((variant) => {
    return getMdiMidnightGroupStatus(variant, now) === 'live';
  });

  if (activeVariant) {
    return activeVariant;
  }

  const upcomingVariant = GROUP_VARIANTS_BY_START.find((variant) => {
    return now < Date.parse(variant.broadcasts[variant.broadcasts.length - 1].endsAt);
  });
  if (upcomingVariant) {
    return upcomingVariant;
  }

  const pastVariants = GROUP_VARIANTS_BY_START.filter((variant) => {
    return now > Date.parse(variant.broadcasts[variant.broadcasts.length - 1].endsAt);
  });
  return pastVariants[pastVariants.length - 1] || GROUP_VARIANTS_BY_START[0];
}

function getLiveMdiMidnightGroupBroadcast(variant: TMdiGroupVariant, now: number) {
  return variant.broadcasts.find((broadcast) => {
    const startsAt = Date.parse(broadcast.startsAt);
    const endsAt = Date.parse(broadcast.endsAt);

    return now >= startsAt && now < endsAt;
  });
}

function getNextMdiMidnightGroupBroadcast(variant: TMdiGroupVariant, now: number) {
  return variant.broadcasts.find((broadcast) => now < Date.parse(broadcast.startsAt));
}

function getSelectedMdiMidnightGroupBroadcast(variant: TMdiGroupVariant, now: number) {
  return (
    getLiveMdiMidnightGroupBroadcast(variant, now) ||
    getNextMdiMidnightGroupBroadcast(variant, now) ||
    variant.broadcasts[variant.broadcasts.length - 1]
  );
}

export function getMdiMidnightGroupBroadcastDayNumber(variant: TMdiGroupVariant, broadcast: TMdiGroupBroadcast) {
  const broadcastIndex = variant.broadcasts.findIndex(
    (variantBroadcast) => variantBroadcast.startsAt === broadcast.startsAt
  );
  return broadcastIndex === -1 ? 1 : broadcastIndex + 1;
}

function getStatusTransitionAt(variant: TMdiGroupVariant, status: TMdiGroupStatus, now: number): number | null {
  const selectedBroadcast = getSelectedMdiMidnightGroupBroadcast(variant, now);

  if (status === 'upcoming') {
    return Date.parse(selectedBroadcast.startsAt);
  }

  if (status === 'live') {
    return Date.parse(selectedBroadcast.endsAt);
  }

  return null;
}

function getButtonLabelKey(status: TMdiGroupStatus) {
  if (status === 'live') {
    return 'liveButton';
  }

  if (status === 'upcoming') {
    return 'upcomingButton';
  }

  return 'completedButton';
}

export function getMdiMidnightGroupsUpsellClickTrackingPayload(
  variant: TMdiGroupVariant,
  status: TMdiGroupStatus,
  part: TMdiGroupClickPart,
  linkText: string,
  linkClasses = ''
) {
  const destinationPath = part === 'bracket' ? variant.bracketUrl : variant.watchUrl;

  return {
    link_url: getTrackedInternalLinkUrl(destinationPath),
    link_text: linkText,
    link_classes: linkClasses,
    link_id: `${ANALYTICS_PREFIX}:${part}:${variant.analyticsKey}`,
    content_type: 'upsell',
    content_id: `${ANALYTICS_PREFIX}:${variant.analyticsKey}`,
    upsell_id: ANALYTICS_PREFIX,
    upsell_part: part,
    upsell_variant: variant.analyticsKey,
    upsell_status: status,
    destination_path: destinationPath
  };
}

export function MdiMidnightGroupsUpsell(props: TMdiMidnightGroupsUpsellProps) {
  const { t, i18n } = useTranslation();
  const isMobile = useMedia(['(max-width: 767px)'], [true], false);
  const isStackedCta = useMedia(['(max-width: 1099px)'], [true], false);
  const shouldStackCta = !isMobile && isStackedCta;
  const [currentTime, setCurrentTime] = useState(Date.now);

  Object.entries(UPSELL_TRANSLATIONS).forEach(([lang, translations]) => {
    if (!i18n.hasResourceBundle(lang, NAMESPACE)) {
      i18n.addResourceBundle(lang, NAMESPACE, translations);
    }
  });

  const selectedGroup = getSelectedMdiMidnightGroupVariant(currentTime);
  const selectedStatus = getMdiMidnightGroupStatus(selectedGroup, currentTime);
  const selectedBroadcast = getSelectedMdiMidnightGroupBroadcast(selectedGroup, currentTime);
  const selectedBroadcastDayNumber = getMdiMidnightGroupBroadcastDayNumber(selectedGroup, selectedBroadcast);
  const nextStatusTransitionAt = getStatusTransitionAt(selectedGroup, selectedStatus, currentTime);
  const countdownLabelText = t(
    `${NAMESPACE}:${selectedBroadcastDayNumber === 1 ? 'startsInLabel' : 'dayStartsInLabel'}`,
    {
      dayNumber: selectedBroadcastDayNumber,
      label: selectedGroup.label
    }
  );
  const headlineText = t(`${NAMESPACE}:headline`, { label: selectedGroup.label });
  const watchPromptText = t(`${NAMESPACE}:watchPrompt`, { label: selectedGroup.label });
  const bodyLinkTextByStatus: Record<TMdiGroupStatus, string> = {
    completed: headlineText,
    live: watchPromptText,
    upcoming: countdownLabelText
  };
  const bodyLinkText = bodyLinkTextByStatus[selectedStatus];
  const primaryButtonLabel = t(`${NAMESPACE}:${getButtonLabelKey(selectedStatus)}`);
  const bracketLinkLabel = t(`${NAMESPACE}:bracketLink`, { label: selectedGroup.label });
  const panelImpressionRef = useTrackedImpression<HTMLDivElement>({
    onImpression: () => {
      RIO_TRACK_EVENT('event', 'upsell_impression', {
        content_type: 'upsell',
        content_id: `${ANALYTICS_PREFIX}:${selectedGroup.analyticsKey}`,
        upsell_id: ANALYTICS_PREFIX,
        upsell_variant: selectedGroup.analyticsKey,
        upsell_status: selectedStatus,
        destination_path: selectedGroup.watchUrl
      });
    }
  });

  useEffect(() => {
    if (!nextStatusTransitionAt) {
      return undefined;
    }

    const timeoutMs = Math.max(nextStatusTransitionAt - currentTime, 0) + 1000;
    const timeout = window.setTimeout(() => setCurrentTime(Date.now()), timeoutMs);
    return () => window.clearTimeout(timeout);
  }, [currentTime, nextStatusTransitionAt]);

  const panelBackgroundStyle = {
    ...panelStyle,
    backgroundImage: [
      'radial-gradient(circle at 88% 45%, rgba(86, 202, 255, 0.18) 0%, rgba(86, 202, 255, 0.05) 24%, rgba(86, 202, 255, 0) 50%)',
      'linear-gradient(90deg, rgba(3, 8, 20, 0.97) 0%, rgba(6, 20, 43, 0.94) 37%, rgba(7, 36, 66, 0.76) 68%, rgba(4, 10, 20, 0.76) 100%)',
      `url(${asset(BACKGROUND_PATH, { width: 1800, height: 360, quality: 92 })})`
    ].join(', ')
  };
  const panelResponsiveStyle = {
    ...panelBackgroundStyle,
    justifyContent: isMobile ? 'center' : undefined,
    rowGap: isMobile ? '0.5rem' : undefined
  };
  const logoWrapResponsiveStyle = {
    ...logoWrapStyle,
    marginLeft: isMobile ? 0 : logoWrapStyle.marginLeft
  };
  const logoResponsiveStyle: React.CSSProperties = {
    ...logoStyle,
    width: isMobile ? 108 : logoStyle.width,
    height: isMobile ? 108 : logoStyle.height
  };
  const contentResponsiveStyle: React.CSSProperties = {
    ...contentStyle,
    textAlign: isMobile ? 'center' : 'left',
    maxWidth: shouldStackCta ? '100%' : contentStyle.maxWidth,
    margin: isMobile ? '0 auto' : undefined
  };
  const headlineResponsiveStyle: React.CSSProperties = {
    ...headlineStyle,
    margin: isMobile ? '0 auto 0.28rem' : '0 0 0.28rem 0'
  };
  const countdownBlockResponsiveStyle: React.CSSProperties = {
    ...countdownBlockStyle
  };
  const supportingLineResponsiveStyle: React.CSSProperties = {
    ...supportingLineStyle,
    margin: isMobile ? '0 auto' : undefined
  };
  const buttonColumnStyle: React.CSSProperties = {
    flex: isMobile || shouldStackCta ? '0 0 100%' : '0 0 174px',
    width: isMobile || shouldStackCta ? '100%' : 174,
    textAlign: isMobile || shouldStackCta ? 'center' : undefined
  };
  const buttonGroupResponsiveStyle = {
    ...buttonGroupStyle,
    alignItems: isMobile || shouldStackCta ? 'center' : 'stretch',
    transform: isMobile || shouldStackCta ? undefined : 'translateX(-0.35rem)'
  };

  const trackUpsellClick = (e: React.MouseEvent<HTMLElement>, part: TMdiGroupClickPart, linkText: string) => {
    const trackingPayload = getMdiMidnightGroupsUpsellClickTrackingPayload(
      selectedGroup,
      selectedStatus,
      part,
      linkText,
      e.currentTarget.className || ''
    );

    RIO_TRACK_EVENT('event', 'upsell_click', trackingPayload);
    return trackingPayload;
  };

  const trackBodyClick = (e: React.MouseEvent<HTMLElement>) => {
    const trackingPayload = trackUpsellClick(e, 'body', bodyLinkText);

    RIO_TRACK_EVENT('event', 'internal_click', trackingPayload);
  };

  const syncGroupStatus = () => {
    setCurrentTime(Date.now());
  };

  const handlePanelClick = (e: React.MouseEvent<HTMLDivElement>) => {
    trackBodyClick(e);
    navigateTo(selectedGroup.watchUrl, e);
  };

  const handleTextClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    trackBodyClick(e);
    navigateTo(selectedGroup.watchUrl, e);
  };

  return (
    <div className={props.className}>
      <div
        ref={panelImpressionRef}
        className="slds-p-around--x-small slds-grid slds-grid--vertical-align-center slds-wrap"
        onClick={handlePanelClick}
        style={panelResponsiveStyle}
      >
        <div className="slds-col slds-shrink-none slds-align--absolute-center rio-m-top-mobi--small">
          <span style={logoWrapResponsiveStyle}>
            <img
              src={asset(LOGO_PATH, { width: 236, height: 236 })}
              alt={t(`${NAMESPACE}:logoAlt`)}
              width={118}
              height={118}
              style={logoResponsiveStyle}
            />
          </span>
        </div>
        <div
          className="slds-col rio-m-top-mobi--small slds-p-horizontal--small"
          onClick={handleTextClick}
          style={contentResponsiveStyle}
        >
          {selectedStatus === 'upcoming' ? (
            <div style={countdownBlockResponsiveStyle}>
              <Typography.Title component="span" size="small" strong transform="uppercase" style={countdownLabelStyle}>
                <i className="fa fa-stopwatch" />
                {countdownLabelText}
              </Typography.Title>
              <Typography.Title component="span" size="large" strong color="white" style={countdownValueStyle}>
                <CountdownTimer
                  to={selectedBroadcast.startsAt}
                  formatter={formatCountdownMsTime}
                  onComplete={syncGroupStatus}
                  tag="span"
                />
              </Typography.Title>
            </div>
          ) : selectedStatus === 'live' ? (
            <div style={liveLabelBlockStyle}>
              <Typography.Title component="span" size="medium" strong style={liveWatchPromptStyle}>
                <i className="fa fa-play-circle" />
                {watchPromptText}
              </Typography.Title>
            </div>
          ) : (
            <Typography.Title size="medium" strong style={headlineResponsiveStyle}>
              {headlineText}
            </Typography.Title>
          )}
          <Typography.Text component="div" size="large" style={supportingLineResponsiveStyle}>
            <Trans
              i18nKey={`${NAMESPACE}:${selectedStatus === 'live' ? 'liveSupportingLine' : 'supportingLine'}`}
              values={{ label: selectedGroup.label }}
              components={[<b />, <b />, <b />]}
            />
          </Typography.Text>
        </div>
        <div
          className="slds-col slds-grow slds-p-vertical--small slds-p-right--small slds-align--absolute-center rio-m-top-mobi--small"
          style={buttonColumnStyle}
        >
          <div style={buttonGroupResponsiveStyle}>
            <Link
              className={ctaButtonClassName}
              to={selectedGroup.watchUrl}
              as={`${ANALYTICS_PREFIX}:button:${selectedGroup.analyticsKey}`}
              onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                e.stopPropagation();
                trackUpsellClick(e, 'button', primaryButtonLabel);
              }}
              style={primaryButtonStyle}
            >
              {primaryButtonLabel}
            </Link>
            <Link
              className={secondaryLinkClassName}
              to={selectedGroup.bracketUrl}
              as={`${ANALYTICS_PREFIX}:bracket:${selectedGroup.analyticsKey}`}
              onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                e.stopPropagation();
                trackUpsellClick(e, 'bracket', bracketLinkLabel);
              }}
              style={supportLinkStyle}
            >
              {bracketLinkLabel}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
